/**
 * @file totals.js
 * @description Pure invoice math — totals, VAT breakdown, line items.
 *
 * Behaviour preserved exactly from the original calculateTotals() inside
 * invoice-creation-wizard/index.jsx. No business-rule changes.
 *
 * No React. No state. No side effects.
 */

// ---------------------------------------------------------------------------
// Internal: compute the per-line "allowance/charge effect"
// ---------------------------------------------------------------------------

/**
 * Returns the signed amount to add to the line subtotal:
 *  - negative  → allowance (discount)
 *  - positive  → charge
 *  - 0         → indicator missing/unknown
 */
const computeAllowanceChargeEffect = (item) => {
  const indicator = item.allowanceChargeIndicator;
  const base = parseFloat(item.allowanceChargeBaseAmount) || 0;
  const mult = parseFloat(item.allowanceChargeMultiplier) || 0;
  const flat = parseFloat(item.allowanceChargeAmount) || 0;

  if (indicator === "false") {
    return base && mult ? -(base * (mult / 100)) : -flat;
  }
  if (indicator === "true") {
    return base && mult ? base * (mult / 100) : flat;
  }
  return 0;
};

// ---------------------------------------------------------------------------
// Public: totals
// ---------------------------------------------------------------------------

/**
 * Sum a list of line items into the running totals object.
 *
 * @param {Array} lineItems
 * @returns {{ subtotal, totalDiscount, taxableAmount, totalTax, total }}
 */
export const calculateTotals = (lineItems) =>
  lineItems.reduce(
    (acc, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const acEffect = computeAllowanceChargeEffect(item);
      const adjusted = subtotal + acEffect;
      const tax = item.taxExempt
        ? 0
        : adjusted * (parseFloat(item.taxRate) / 100);

      return {
        subtotal: acc.subtotal + subtotal,
        totalDiscount:
          acc.totalDiscount + Math.abs(acEffect < 0 ? acEffect : 0),
        taxableAmount: acc.taxableAmount + (item.taxExempt ? 0 : adjusted),
        totalTax: acc.totalTax + tax,
        total: acc.total + adjusted + tax,
      };
    },
    {
      subtotal: 0,
      totalDiscount: 0,
      taxableAmount: 0,
      totalTax: 0,
      total: 0,
    },
  );

// ---------------------------------------------------------------------------
// Public: VAT breakdown
// ---------------------------------------------------------------------------

/**
 * Group line items by VAT category + rate to produce the ZATCA VAT breakdown.
 *
 * @param {Array} lineItems
 * @returns {Array<{ taxableAmount, taxAmount, vatCategoryCode, vatRate, exemptionReason, exemptionReasonCode }>}
 */
export const calculateVatBreakdown = (lineItems) =>
  Object.values(
    lineItems.reduce((acc, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const acEffect = computeAllowanceChargeEffect(item);
      const adjusted = subtotal + acEffect;
      const tax = item.taxExempt
        ? 0
        : adjusted * (parseFloat(item.taxRate) / 100);

      const catCode = item.taxExempt ? item.vatCategoryCode || "Z" : "S";
      const key = `${catCode}_${item.taxRate}`;

      if (!acc[key]) {
        acc[key] = {
          taxableAmount: 0,
          taxAmount: 0,
          vatCategoryCode: catCode,
          vatRate: item.taxRate,
          exemptionReason: item.exemptionReason || "",
          exemptionReasonCode: item.exemptionReasonCode || "",
        };
      }
      acc[key].taxableAmount += item.taxExempt ? 0 : adjusted;
      acc[key].taxAmount += tax;
      return acc;
    }, {}),
  );

// ---------------------------------------------------------------------------
// Public: invoice lines in ZATCA shape
// ---------------------------------------------------------------------------

/**
 * Map UI line items → ZATCA invoiceLines payload shape.
 *
 * @param {Array} lineItems
 * @returns {Array}
 */
export const calculateInvoiceLines = (lineItems) =>
  lineItems.map((item, index) => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount =
      item.discountType === "percentage"
        ? subtotal * (item.discount / 100)
        : item.discount;
    const discounted = subtotal - discountAmount;
    const tax = item.taxExempt ? 0 : discounted * (item.taxRate / 100);

    const taxCategory = {
      id: item.taxExempt ? "Z" : "S",
      percent: item.taxExempt ? 0 : item.taxRate,
    };

    return {
      lineId: (index + 1).toString(),
      invoicedQuantity: parseFloat(item.quantity),
      unitCode: "PCE",
      lineExtensionAmount: discounted,
      item: {
        name: item.description,
        classifiedTaxCategory: taxCategory,
      },
      price: {
        priceAmount: item.unitPrice,
        allowanceCharge: {
          chargeIndicator: false,
          allowanceChargeReason: item.discount > 0 ? "Discount applied" : "",
          amount: discountAmount,
        },
      },
      allowancesCharges:
        item.discount > 0
          ? [
              {
                chargeIndicator: true,
                amount: discountAmount,
                baseAmount: subtotal,
                multiplierFactorNumeric:
                  item.discountType === "percentage"
                    ? item.discount
                    : (discountAmount / subtotal) * 100,
                allowanceChargeReason: "Line item discount",
                taxCategory,
              },
            ]
          : [
              {
                chargeIndicator: false,
                amount: 0.0,
                baseAmount: 0.0,
                multiplierFactorNumeric:
                  item.discountType === "percentage"
                    ? item.discount
                    : (discountAmount / subtotal) * 100,
                allowanceChargeReason: "-",
                taxCategory,
              },
            ],
      taxTotal: { taxAmount: tax, roundingAmount: discounted + tax },
    };
  });